import * as React from 'react'
import { Fragment, useState } from 'react'
import ReactHover, { Hover, Trigger } from 'react-hover'
import { CalendarElementColumns, CalendarHelper, WarehouseCalendarHeadRows } from './calendar'
import ElementInfo from './element_info'
import QuantityModal from './quantity_modal'
import { Element, RentList, RentItem } from './warehouse_rent_list_types'

interface WarehouseCalendarTableProps {
  startDate: string
  endDate: string
  groupedAvailableElements: Record<string, Element[]>
  elementGroups: Record<string, string>
  handleElementClick: (element: Element, quantity: number) => void
  rentedItems: Record<string, RentItem[]>
  rentList: RentList
}

function WarehouseCalendarTable({
  startDate,
  endDate,
  groupedAvailableElements,
  elementGroups,
  handleElementClick,
  rentedItems,
  rentList,
}: WarehouseCalendarTableProps): React.ReactElement {
  const cal = new CalendarHelper(startDate, endDate)
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    element: Element | null
    maxQuantity: number
    currentQuantity: number
    isParentMissing: boolean
  }>({
    isOpen: false,
    element: null,
    maxQuantity: 0,
    currentQuantity: 0,
    isParentMissing: false,
  })

  const getCurrentRentData = (element: Element) => {
    const allItems = rentedItems[element.id] || []
    const items = allItems.filter((item) => item.rentList && item.rentList.id === rentList.id)
    const quantity = items.reduce((total, item) => total + (item.quantity || 1), 0)

    return {
      items,
      quantity,
    }
  }

  const handleElementClickToggle = (element: Element) => {
    const { quantity: currentQuantity } = getCurrentRentData(element)
    const totalQty = element.amount || 0
    const availableQty = element.availableQuantity !== undefined ? element.availableQuantity : totalQty

    const isParentMissing = element.parentId
      ? !(rentedItems[element.parentId] || []).some(
          (item) => item.rentList && item.rentList.id === rentList.id && (item.quantity || 0) > 0
        )
      : false

    // Maximum quantity = already booked in current rent_list + additional available
    // availableQty = total - booked_in_other_rent_lists
    // maxQuantity = availableQty (this already includes what we can book in current rent_list)
    const maxQuantity = availableQty

    setModalState({
      isOpen: true,
      element,
      maxQuantity,
      currentQuantity,
      isParentMissing,
    })
  }

  const handleModalConfirm = async (quantity: number) => {
    const { element } = modalState
    if (!element) return

    // Close modal first
    setModalState({
      isOpen: false,
      element: null,
      maxQuantity: 0,
      currentQuantity: 0,
      isParentMissing: false,
    })

    try {
      // Always use rent_element - it handles both create and update
      // If quantity is 0, the backend will delete the item
      // If quantity > 0, the backend will create or update the item
      handleElementClick(element, quantity)
    } catch (error) {
      console.error('Error in modal confirm:', error)
    }
  }

  const handleModalCancel = () => {
    setModalState({
      isOpen: false,
      element: null,
      maxQuantity: 0,
      currentQuantity: 0,
      isParentMissing: false,
    })
  }

  return (
    <>
      <table className="rent-list-table table table-bordered table-hover">
        <thead>
          <WarehouseCalendarHeadRows startDate={startDate} endDate={endDate} />
        </thead>
        <tbody>
          {Object.keys(groupedAvailableElements).map((groupId) => (
            <Fragment key={`group-${groupId}`}>
              <tr className="rent-list-table-group-row">
                <td className="rent-list-table-element-cell" colSpan={cal.totalDays() + 2}>
                  <span className="rent-list-table-element-cell-content">
                    <b>{elementGroups[groupId]}</b>
                  </span>
                </td>
              </tr>
              {groupedAvailableElements[groupId].map((element) => {
                const { quantity: currentRentedQty } = getCurrentRentData(element)
                const totalQty = element.amount || 0
                const availableQty = element.availableQuantity !== undefined ? element.availableQuantity : totalQty

                return (
                  <tr
                    key={`element-${element.id}`}
                    onClick={() => handleElementClickToggle(element)}
                    className="rent-list-table-clickable-row"
                  >
                    <td className="rent-list-table-element-cell">
                      <ReactHover
                        options={{
                          followCursor: true,
                          shiftX: 20,
                          shiftY: 0,
                        }}
                      >
                        <Trigger type="trigger">
                          <span className={`rent-list-table-element-cell-content ${element.parentId ? 'pl-4' : ''}`}>
                            {element.name}
                          </span>
                        </Trigger>
                        <Hover type="hover">
                          <ElementInfo element={element} />
                        </Hover>
                      </ReactHover>
                    </td>
                    <td className="rent-list-table-quantity-cell">
                      <span className="rent-list-table-element-cell-content">
                        {Math.max(0, availableQty - currentRentedQty)}
                      </span>
                    </td>
                    <CalendarElementColumns
                      element={element}
                      startDate={startDate}
                      endDate={endDate}
                      rentedItems={rentedItems}
                      rentList={rentList}
                    />
                  </tr>
                )
              })}
            </Fragment>
          ))}
        </tbody>
      </table>

      <QuantityModal
        isOpen={modalState.isOpen}
        element={modalState.element}
        maxQuantity={modalState.maxQuantity}
        currentQuantity={modalState.currentQuantity}
        isParentMissing={modalState.isParentMissing}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </>
  )
}

export default WarehouseCalendarTable
